import type { Room } from "@/server/models/room";
import {
  getReversedSquareId,
  getSquareId,
  PLAYER_COLOR,
  TILE_STATE,
  type BoardSquareData,
  type BoardSquareID,
  type PlayerColor,
  type TileState,
} from "./room";

export type Move = [number, number] | [number, number, CustomMove];

export const CUSTOM_MOVES = {
  EN_PASSANT: "en-passant",
  CASTLE_NEAR: "castle-near",
  CASTLE_FAR: "castle-far",
} as const;
export type CustomMove = (typeof CUSTOM_MOVES)[keyof typeof CUSTOM_MOVES];

export function getPossibleOpponentMoveLocations(data: {
  board: Room["board"];
  userColor: PlayerColor;
  turn: number;
  isPlayerKingInCheck?: boolean;
}) {
  const isPlayerKingInCheck = data.isPlayerKingInCheck ?? false;
  const opponentPieceLocations: [number, number][] = [];

  for (let row = 0; row < 8; row += 1) {
    for (let column = 0; column < 8; column += 1) {
      const piece = data.board.get(getSquareId(row, column));

      if (
        !!piece &&
        piece.state !== TILE_STATE.EMPTY &&
        piece.color !== data.userColor
      ) {
        opponentPieceLocations.push([row, column]);
      }
    }
  }

  const movesSet = new Set<BoardSquareID>();

  opponentPieceLocations.forEach((square) => {
    const moves = getAvailableMoves({
      board: data.board,
      square,
      turn: data.turn,
      isPlayerKingInCheck,
    });

    moves.forEach((move) => {
      movesSet.add(getSquareId(move[0], move[1]));
    });
  });

  return Array.from(movesSet).map(
    (move) => move.split(":").map((num) => parseInt(num)) as [number, number]
  );
}

export function getPiecesAttackingUserKing(data: {
  board: Room["board"];
  userColor: PlayerColor;
  turn: number;
}) {
  let kingLocation: [number, number] = [-1, -1];
  const opponentPieceLocations: [number, number][] = [];

  for (let row = 0; row < 8; row += 1) {
    for (let column = 0; column < 8; column += 1) {
      const piece = data.board.get(getSquareId(row, column));

      if (!piece || piece.state === TILE_STATE.EMPTY) {
        continue;
      }

      if (piece.color === data.userColor && piece.state === TILE_STATE.KING) {
        kingLocation = [row, column];
        continue;
      }

      if (piece.color !== data.userColor) {
        opponentPieceLocations.push([row, column]);
      }
    }
  }

  const pieceLocationsAttackingTheKing: [number, number][] = [];

  opponentPieceLocations.forEach((square) => {
    const moves = getAvailableMoves({
      board: data.board,
      square,
      turn: data.turn,
      isPlayerKingInCheck: false,
    });

    if (
      moves.some(
        (move) => move[0] === kingLocation[0] && move[1] === kingLocation[1]
      )
    ) {
      pieceLocationsAttackingTheKing.push(square);
    }
  });

  return pieceLocationsAttackingTheKing;
}

export function getIsKingInCheck(
  board: Room["board"],
  playerColor: PlayerColor,
  possibleDestinationsForOpponent: Move[]
) {
  let kingLocation: [number, number] | undefined = undefined;

  board.forEach((data, key) => {
    if (data.state === TILE_STATE.KING && data.color === playerColor) {
      kingLocation = key.split(":").map((num) => parseInt(num)) as [
        number,
        number,
      ];
    }
  });

  if (!kingLocation) return false;

  return possibleDestinationsForOpponent.some(
    (destination) =>
      destination[0] === kingLocation![0] && destination[1] === kingLocation![1]
  );
}

export function flipBoard(board: Room["board"]) {
  const newBoard = new Map<BoardSquareID, BoardSquareData>();

  board.forEach((square, key) => {
    if (!key || !square) return;

    const [row, column] = key!.split(":");
    newBoard.set(
      getReversedSquareId(parseInt(row!), parseInt(column!)),
      square
    );
  });

  return newBoard;
}

export function getPlayerTurn(turn: number) {
  return turn % 2 === 0 ? PLAYER_COLOR.WHITE : PLAYER_COLOR.BLACK;
}

function getSquareAt(board: Room["board"], row: number, column: number) {
  return board.get(getSquareId(row, column));
}

function isPieceAt(board: Room["board"], row: number, column: number) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && piece.state !== TILE_STATE.EMPTY;
}

function getPieceAt(board: Room["board"], row: number, column: number) {
  return board.get(getSquareId(row, column));
}

function isPiece(board: Room["board"], row: number, column: number) {
  const piece = board.get(getSquareId(row, column));
  return (
    !!piece &&
    piece.state !== TILE_STATE.EMPTY &&
    piece.state !== TILE_STATE.GAP
  );
}

function isEmpty(board: Room["board"], row: number, column: number) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && piece.state === TILE_STATE.EMPTY;
}

function isGap(board: Room["board"], row: number, column: number) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && piece.state === TILE_STATE.GAP;
}

function isFriendlyPieceAt(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && isPiece(board, row, column) && piece?.color === playerColor;
}

function isEnemyPieceAt(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && isPiece(board, row, column) && piece?.color !== playerColor;
}

function isEmptyOrEnemyPieceAt(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  const piece = board.get(getSquareId(row, column));

  const isEnemyPiece =
    isPiece(board, row, column) && piece?.color !== playerColor;
  const isEmpty = piece?.state === TILE_STATE.EMPTY;

  return !!piece && (isEmpty || isEnemyPiece);
}

function isEnemyPieceOfTypeAt(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number,
  pieceType: TileState
) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && piece.state === pieceType && piece?.color !== playerColor;
}

function isFriendlyPieceOfTypeAt(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number,
  pieceType: TileState
) {
  const piece = board.get(getSquareId(row, column));
  return !!piece && piece.state === pieceType && piece?.color === playerColor;
}

function getEastMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];
  for (let eastIndex = column + 1; eastIndex < 8; eastIndex += 1) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, row, eastIndex)) {
      moves.push([row, eastIndex]);
    }

    if (isPieceAt(board, row, eastIndex)) {
      break;
    }
  }
  return moves;
}

function getWestMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];
  for (let westIndex = column - 1; westIndex >= 0; westIndex -= 1) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, row, westIndex)) {
      moves.push([row, westIndex]);
    }

    if (isPieceAt(board, row, westIndex)) {
      break;
    }
  }
  return moves;
}

function getNorthMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];
  for (let northIndex = row + 1; northIndex < 8; northIndex += 1) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, northIndex, column)) {
      moves.push([northIndex, column]);
    }
    if (isPieceAt(board, northIndex, column)) {
      break;
    }
  }
  return moves;
}

function getSouthMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];
  for (let southIndex = row - 1; southIndex >= 0; southIndex -= 1) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, southIndex, column)) {
      moves.push([southIndex, column]);
    }

    if (isPieceAt(board, southIndex, column)) {
      break;
    }
  }
  return moves;
}

function getNorthEastMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];

  let rowIndex = row - 1;
  let columnIndex = column + 1;

  while (rowIndex >= 0 && columnIndex < 8) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, rowIndex, columnIndex)) {
      moves.push([rowIndex, columnIndex]);
    }

    if (isPieceAt(board, rowIndex, columnIndex)) {
      break;
    }

    rowIndex -= 1;
    columnIndex += 1;
  }

  return moves;
}

function getNorthWestMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];

  let rowIndex = row - 1;
  let columnIndex = column - 1;

  while (rowIndex >= 0 && columnIndex >= 0) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, rowIndex, columnIndex)) {
      moves.push([rowIndex, columnIndex]);
    }

    if (isPieceAt(board, rowIndex, columnIndex)) {
      break;
    }

    rowIndex -= 1;
    columnIndex -= 1;
  }

  return moves;
}

function getSouthWestMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];

  let rowIndex = row + 1;
  let columnIndex = column - 1;

  while (rowIndex < 8 && columnIndex >= 0) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, rowIndex, columnIndex)) {
      moves.push([rowIndex, columnIndex]);
    }

    if (isPieceAt(board, rowIndex, columnIndex)) {
      break;
    }

    rowIndex += 1;
    columnIndex -= 1;
  }

  return moves;
}

function getSouthEastMoves(
  board: Room["board"],
  playerColor: PlayerColor,
  row: number,
  column: number
) {
  let moves: Move[] = [];

  let rowIndex = row + 1;
  let columnIndex = column + 1;

  while (rowIndex < 8 && columnIndex < 8) {
    if (isEmptyOrEnemyPieceAt(board, playerColor, rowIndex, columnIndex)) {
      moves.push([rowIndex, columnIndex]);
    }

    if (isPieceAt(board, rowIndex, columnIndex)) {
      break;
    }

    rowIndex += 1;
    columnIndex += 1;
  }

  return moves;
}

interface MoveProps {
  board: Room["board"];
  row: number;
  column: number;
  pieceData: BoardSquareData;
  turn: number;
  playerTurn: ReturnType<typeof getPlayerTurn>;
}

function isPossibleEnemyMoveAt(
  possibleDestinationsForOpponent: Move[],
  row: number,
  column: number
) {
  return possibleDestinationsForOpponent.some((destination) => {
    return destination[0] === row && destination[1] === column;
  });
}

function createChecker(props: MoveProps) {
  const playerColor = getPlayerTurn(props.turn);

  return {
    isPieceAt: (row: number, column: number) =>
      isPieceAt(props.board, row, column),
    getPieceAt: (row: number, column: number) =>
      getPieceAt(props.board, row, column),
    isEmpty: (row: number, column: number) => isEmpty(props.board, row, column),
    isGap: (row: number, column: number) => isGap(props.board, row, column),
    isEnemyPieceAt: (row: number, column: number) =>
      isEnemyPieceAt(props.board, playerColor, row, column),
    isFriendlyPieceAt: (row: number, column: number) =>
      isFriendlyPieceAt(props.board, playerColor, row, column),
    isEmptyOrEnemyPieceAt: (row: number, column: number) =>
      isEmptyOrEnemyPieceAt(props.board, playerColor, row, column),
    isEnemyPieceOfTypeAt: (row: number, column: number, pieceType: TileState) =>
      isEnemyPieceOfTypeAt(props.board, playerColor, row, column, pieceType),
    isFriendlyPieceOfTypeAt: (
      row: number,
      column: number,
      pieceType: TileState
    ) =>
      isFriendlyPieceOfTypeAt(props.board, playerColor, row, column, pieceType),
    getEastMoves: (row: number, column: number) =>
      getEastMoves(props.board, playerColor, row, column),
    getWestMoves: (row: number, column: number) =>
      getWestMoves(props.board, playerColor, row, column),
    getNorthMoves: (row: number, column: number) =>
      getNorthMoves(props.board, playerColor, row, column),
    getSouthMoves: (row: number, column: number) =>
      getSouthMoves(props.board, playerColor, row, column),
    getSouthEastMoves: (row: number, column: number) =>
      getSouthEastMoves(props.board, playerColor, row, column),
    getSouthWestMoves: (row: number, column: number) =>
      getSouthWestMoves(props.board, playerColor, row, column),
    getNorthEastMoves: (row: number, column: number) =>
      getNorthEastMoves(props.board, playerColor, row, column),
    getNorthWestMoves: (row: number, column: number) =>
      getNorthWestMoves(props.board, playerColor, row, column),
  };
}

function getPawnMoves(props: MoveProps): Move[] {
  const checker = createChecker(props);
  const { board, row, column, pieceData, turn } = props;

  let moves: Move[] = [];

  // Basic moves
  if (checker.isEmpty(row - 1, column)) {
    moves.push([row - 1, column]);

    if (checker.isEmpty(row - 2, column) && !pieceData.lastMoveOnTurn) {
      moves.push([row - 2, column]);
    }
  }

  // Attack top left
  if (checker.isEnemyPieceAt(row - 1, column - 1)) {
    moves.push([row - 1, column - 1]);
  }

  // Attack top right
  if (checker.isEnemyPieceAt(row - 1, column + 1)) {
    moves.push([row - 1, column + 1]);
  }

  // en passant left
  if (
    checker.isEnemyPieceOfTypeAt(row, column - 1, TILE_STATE.PAWN) &&
    getSquareAt(board, row, column - 1)?.lastMoveOnTurn === turn &&
    !!turn
  ) {
    moves.push([row - 1, column - 1, CUSTOM_MOVES.EN_PASSANT]);
  }

  // en passant right
  if (
    checker.isEnemyPieceOfTypeAt(row, column + 1, TILE_STATE.PAWN) &&
    getSquareAt(board, row, column + 1)?.lastMoveOnTurn === turn &&
    !!turn
  ) {
    moves.push([row - 1, column + 1, CUSTOM_MOVES.EN_PASSANT]);
  }

  return moves;
}

function getRookMoves(props: MoveProps): Move[] {
  const checker = createChecker(props);
  const { board, row, column, pieceData, turn } = props;

  let moves: Move[] = [];

  moves.push(...checker.getSouthMoves(row, column));
  moves.push(...checker.getNorthMoves(row, column));
  moves.push(...checker.getEastMoves(row, column));
  moves.push(...checker.getWestMoves(row, column));

  return moves;
}

function getKnightMoves(props: MoveProps): Move[] {
  const checker = createChecker(props);
  const { board, row, column, pieceData, turn } = props;

  let moves: Move[] = [];

  if (checker.isEmptyOrEnemyPieceAt(row - 2, column - 1)) {
    moves.push([row - 2, column - 1]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row - 2, column + 1)) {
    moves.push([row - 2, column + 1]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row - 1, column + 2)) {
    moves.push([row - 1, column + 2]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row + 1, column + 2)) {
    moves.push([row + 1, column + 2]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row + 2, column + 1)) {
    moves.push([row + 2, column + 1]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row + 2, column - 1)) {
    moves.push([row + 2, column - 1]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row + 1, column - 2)) {
    moves.push([row + 1, column - 2]);
  }
  if (checker.isEmptyOrEnemyPieceAt(row - 1, column - 2)) {
    moves.push([row - 1, column - 2]);
  }

  return moves;
}

function getBishopMoves(props: MoveProps): Move[] {
  const checker = createChecker(props);
  const { board, row, column, pieceData, turn } = props;

  let moves: Move[] = [];

  moves.push(...checker.getSouthEastMoves(row, column));
  moves.push(...checker.getSouthWestMoves(row, column));
  moves.push(...checker.getNorthEastMoves(row, column));
  moves.push(...checker.getNorthWestMoves(row, column));

  return moves;
}

function getQueenMoves(props: MoveProps): Move[] {
  const checker = createChecker(props);
  const { board, row, column, pieceData, turn } = props;

  let moves: Move[] = [];

  moves.push(...checker.getSouthMoves(row, column));
  moves.push(...checker.getNorthMoves(row, column));
  moves.push(...checker.getEastMoves(row, column));
  moves.push(...checker.getWestMoves(row, column));

  moves.push(...checker.getSouthEastMoves(row, column));
  moves.push(...checker.getSouthWestMoves(row, column));
  moves.push(...checker.getNorthEastMoves(row, column));
  moves.push(...checker.getNorthWestMoves(row, column));

  return moves;
}

function getKingMoves(props: MoveProps): Move[] {
  const checker = createChecker(props);
  const { board, row, column, pieceData, turn } = props;

  const playerColor = getPlayerTurn(props.turn);
  const isPlayerWhite = playerColor === PLAYER_COLOR.WHITE;

  let moves: Move[] = [];

  const leftCastleDistance = isPlayerWhite ? 4 : 3;
  const leftCastleType = isPlayerWhite
    ? CUSTOM_MOVES.CASTLE_FAR
    : CUSTOM_MOVES.CASTLE_NEAR;
  const possibleLeftRook = checker.getPieceAt(row, column - leftCastleDistance);

  if (
    checker.isEmpty(row, column - 1) &&
    checker.isEmpty(row, column - 2) &&
    (isPlayerWhite ? checker.isEmpty(row, column - 3) : true) &&
    pieceData.lastMoveOnTurn === 0 &&
    possibleLeftRook?.state === TILE_STATE.ROOK &&
    possibleLeftRook?.lastMoveOnTurn === 0
  ) {
    const offset = leftCastleType === CUSTOM_MOVES.CASTLE_FAR ? 2 : 1;
    moves.push([row, column - (leftCastleDistance - offset), leftCastleType]);
  }

  const rightCastleDistance = isPlayerWhite ? 3 : 4;
  const rightCastleType = isPlayerWhite
    ? CUSTOM_MOVES.CASTLE_NEAR
    : CUSTOM_MOVES.CASTLE_FAR;
  const possibleRightRook = checker.getPieceAt(
    row,
    column + rightCastleDistance
  );

  if (
    checker.isEmpty(row, column + 1) &&
    checker.isEmpty(row, column + 2) &&
    (isPlayerWhite ? true : checker.isEmpty(row, column + 3)) &&
    pieceData.lastMoveOnTurn === 0 &&
    possibleRightRook?.state === TILE_STATE.ROOK &&
    possibleRightRook?.lastMoveOnTurn === 0
  ) {
    const offset = rightCastleType === CUSTOM_MOVES.CASTLE_FAR ? 2 : 1;
    moves.push([row, column + (rightCastleDistance - offset), rightCastleType]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row - 1, column - 1)) {
    moves.push([row - 1, column - 1]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row - 1, column)) {
    moves.push([row - 1, column]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row - 1, column + 1)) {
    moves.push([row - 1, column + 1]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row, column + 1)) {
    moves.push([row, column + 1]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row + 1, column + 1)) {
    moves.push([row + 1, column + 1]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row + 1, column)) {
    moves.push([row + 1, column]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row + 1, column - 1)) {
    moves.push([row + 1, column - 1]);
  }

  if (checker.isEmptyOrEnemyPieceAt(row, column - 1)) {
    moves.push([row, column - 1]);
  }

  return moves;
}

export function getAvailableMoves(data: {
  board: Room["board"];
  square: [number, number];
  turn: Room["turn"];
  isPlayerKingInCheck: boolean;
}): Move[] {
  const { board, square, turn, isPlayerKingInCheck } = data;
  const [row, column] = square;

  const currentSquareId = getSquareId(row, column);
  const pieceData = board.get(currentSquareId);

  if (!pieceData) return [];

  const playerTurn = getPlayerTurn(turn);

  // Moves that the piece could make if no pieces were on the board

  let moves: Move[] = [];
  const props: MoveProps = {
    board,
    row,
    column,
    pieceData,
    turn,
    playerTurn,
  };

  if (pieceData.state === TILE_STATE.PAWN) {
    moves = getPawnMoves(props);
  }
  if (pieceData.state === TILE_STATE.ROOK) {
    moves = getRookMoves(props);
  }
  if (pieceData.state === TILE_STATE.KNIGHT) {
    moves = getKnightMoves(props);
  }
  if (pieceData.state === TILE_STATE.BISHOP) {
    moves = getBishopMoves(props);
  }
  if (pieceData.state === TILE_STATE.QUEEN) {
    moves = getQueenMoves(props);
  }
  if (pieceData.state === TILE_STATE.KING) {
    moves = getKingMoves(props);
  }

  let availableMoves = moves;
  if (isPlayerKingInCheck) {
    availableMoves = moves.reduce((partialMoves, currentMove) => {
      const simulatedBoard = new Map(board);

      simulatedBoard.set(currentSquareId, {
        state: TILE_STATE.EMPTY,
        color: PLAYER_COLOR.NONE,
        image: undefined,
        lastMoveOnTurn: undefined,
      });

      const destinationSquareId = getSquareId(currentMove[0], currentMove[1]);
      simulatedBoard.set(destinationSquareId, pieceData);

      const futureLocationsAttackingUserKing = getPiecesAttackingUserKing({
        board: simulatedBoard,
        userColor: playerTurn,
        turn: turn + 1,
      });

      if (futureLocationsAttackingUserKing.length === 0) {
        partialMoves.push(currentMove);
      }

      return partialMoves;
    }, [] as Move[]);
  }

  return availableMoves;
}

export function getAvailableTileMoves(data: {
  board: Room["board"];
  tile: [number, number];
  turn: Room["turn"];
  isPlayerKingInCheck: boolean;
}): Move[] {
  const { board, tile, turn, isPlayerKingInCheck } = data;
  const [tileRow, tileColumn] = tile;

  const row = tileRow * 2;
  const column = tileColumn * 2;

  const currentSquareId = getSquareId(row, column);
  const pieceData = board.get(currentSquareId);

  if (!pieceData) return [];
  if (pieceData.state === TILE_STATE.GAP) return [];

  const playerTurn = getPlayerTurn(turn);

  // Moves that the piece could make if no pieces were on the board

  let moves: Move[] = [];
  const props: MoveProps = {
    board,
    row,
    column,
    pieceData,
    turn,
    playerTurn,
  };

  const checker = createChecker(props);

  if (checker.isGap(row - 2, column)) {
    moves.push([tileRow - 1, tileColumn]);
  }
  if (checker.isGap(row, column + 2)) {
    moves.push([tileRow, tileColumn + 1]);
  }
  if (checker.isGap(row + 2, column)) {
    moves.push([tileRow + 1, tileColumn]);
  }
  if (checker.isGap(row, column - 2)) {
    moves.push([tileRow, tileColumn - 1]);
  }
  console.log(moves);

  let availableMoves = moves;
  if (isPlayerKingInCheck) {
    availableMoves = moves.reduce((partialMoves, currentMove) => {
      const simulatedBoard = new Map(board);

      simulatedBoard.set(currentSquareId, {
        state: TILE_STATE.EMPTY,
        color: PLAYER_COLOR.NONE,
        image: undefined,
        lastMoveOnTurn: undefined,
      });

      const destinationSquareId = getSquareId(currentMove[0], currentMove[1]);
      simulatedBoard.set(destinationSquareId, pieceData);

      const futureLocationsAttackingUserKing = getPiecesAttackingUserKing({
        board: simulatedBoard,
        userColor: playerTurn,
        turn: turn + 1,
      });

      if (futureLocationsAttackingUserKing.length === 0) {
        partialMoves.push(currentMove);
      }

      return partialMoves;
    }, [] as Move[]);
  }

  return availableMoves;
}

export const TILE_REMOVAL_LOCATIONS = [
  [1, 0],
  [1, 1],
  [1, 2],
  [1, 3],
  [2, 0],
  [2, 1],
  [2, 2],
  [2, 3],
];
